import React from 'react';
import {buildClickTrackingUrl, decodeString, readableReadingTime} from '../_Global/searchHelpers';
import {renderHTML} from '../_Global/searchReactHelpers';
import useSearchStore from '../_Global/SearchStore';

import { DateTime, Settings } from "luxon";

interface ResultGlobalConfiguration {
    result: SearchResult;
}

/**
 * Configure the Luxon time zone so that all
 * instances of DateTime use the "correct" local time.
 * Timezone from: https://www.iana.org/time-zones
 *
 * @source https://moment.github.io/luxon/#/zones?id=changing-the-default-zone
 */
Settings.defaultZone = "Australia/Sydney";

function ResultGlobal({result}: ResultGlobalConfiguration) {
    const type = result?.listMetadata?.type?.[0] ?? '';
    const fileType = result?.listMetadata?.fileType?.[0] ?? '';
    const prettyDate = DateTime.fromISO(result?.listMetadata?.d ?? '').toFormat('dd LLL y');
    const searchTermHighlighting = useSearchStore((state) => state.searchTermHighlighting);

    /**
     * Return the result summary based on the application configuration
     * and the order of preference for the content, as set here.
     */
    function getResultSummaryContent() {
      let searchSummaryContent = decodeString(result['summary']);

      if (searchTermHighlighting) {
        searchSummaryContent = result['summaryHighlighted'] || '';
      }

      if (!searchTermHighlighting && result['summaryTruncated']) {
        searchSummaryContent = result['summaryTruncated'];
      }

      return searchSummaryContent;
    }

    // Output a different result layout depending on the type
    switch (type) {
        // News and Insights share a layout
        case 'Insights':
        case 'News':
            return (
                <div key={result['rank']} className="listing-card no-wysiwyg">
                    <a href={buildClickTrackingUrl(result['clickTrackingUrl'])} className="listing-card__wrapper heading-link__underline">
                        <div className="listing-card__tag card-tag card-tag--alt body-size-lg">
                            <svg className="card-tag__svg">
                                <use xlinkHref="#square"></use>
                            </svg>
                            <span className="card-tag__text">{type}</span>
                        </div>
                        <div className="listing-card__grid grid sm:grid-cols-8 sm:gap-8 md:gap-16 lg:gap-0">
                            <div className="sm:col-span-4 lg:col-span-3">
                                <h3 className="listing-card__heading heading-size-md">{result['title']}</h3>

                                <div className="listing-card__metadata metadata-wrapper metadata-wrapper--inline">
                                    <div className="metadata-block metadata-block--inline">
                                        <svg className="metadata-block__svg body-size-lg">
                                            <use xlinkHref="#calendar"></use>
                                        </svg>
                                        <span className="metadata-block__value body-size-lg">{prettyDate}</span>
                                    </div>
                                    <span className="vertical-divider" role="presentation"></span>
                                    <div className="metadata-block metadata-block--inline">
                                        <span className="metadata-block__value body-size-lg">{readableReadingTime(result['listMetadata']['readingTime'])}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="sm:col-span-4 lg:col-span-3 lg:col-end-8">
                              { renderHTML('p', 'listing-card__text body-size-lg', getResultSummaryContent()) }
                            </div>

                            <div className="listing-card__arrow-link">
                                <svg role="presentation" className="icon__arrow icon__arrow--diagonal-animation">
                                    <use xlinkHref="#arrow-diagonal"></use>
                                </svg>
                            </div>
                        </div>
                    </a>
                </div>
            );
        case 'Document':
            return (
                <div key={result['rank']} className="listing-card no-wysiwyg">
                    <a href={buildClickTrackingUrl(result['clickTrackingUrl'])} className="listing-card__wrapper heading-link__underline">
                        <div className="listing-card__grid grid sm:grid-cols-8 sm:gap-8 md:gap-16 lg:gap-0">
                            <div className="sm:col-span-4 lg:col-span-3">
                                <h3 className="listing-card__heading heading-size-md">{result['title']}</h3>

                                <div className="listing-card__metadata metadata-wrapper metadata-wrapper--inline">
                                    <div className="metadata-block metadata-block--inline">
                                        <svg className="metadata-block__svg body-size-lg">
                                            <use xlinkHref="#document"></use>
                                        </svg>
                                        <span className="metadata-block__value body-size-lg">{fileType}</span>
                                    </div>
                                    {/* <span className="vertical-divider" role="presentation"></span>
                                    <div className="metadata-block metadata-block--inline">
                                        <span className="metadata-block__value body-size-lg">1MB</span>
                                    </div> */}
                                    <span className="vertical-divider" role="presentation"></span>
                                    <div className="metadata-block metadata-block--inline">
                                        <span className="metadata-block__value body-size-lg">Download</span>
                                        <svg className="metadata-block__svg body-size-lg">
                                            <use xlinkHref="#download"></use>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div className="listing-card__arrow-link">
                                <svg role="presentation" className="icon__arrow icon__arrow--diagonal-animation">
                                    <use xlinkHref="#arrow-diagonal"></use>
                                </svg>
                            </div>
                        </div>
                    </a>
                </div>
            );
        case 'Web page':
            return (
                <div key={result['rank']} className="listing-card no-wysiwyg">
                    <a href={buildClickTrackingUrl(result['clickTrackingUrl'])} className="listing-card__wrapper heading-link__underline">
                        <div className="listing-card__tag card-tag card-tag--alt body-size-lg">
                            <svg className="card-tag__svg">
                                <use xlinkHref="#square"></use>
                            </svg>
                            <span className="card-tag__text">{type}</span>
                        </div>
                        <div className="listing-card__grid grid sm:grid-cols-8 sm:gap-8 md:gap-16 lg:gap-0">
                            <div className="sm:col-span-4 lg:col-span-3">
                                <h3 className="listing-card__heading heading-size-md">{result['title']}</h3>

                                <div className="listing-card__metadata metadata-wrapper metadata-wrapper--inline">
                                    <div className="metadata-block metadata-block--inline">
                                        <svg className="metadata-block__svg body-size-lg">
                                            <use xlinkHref="#calendar"></use>
                                        </svg>
                                        <span className="metadata-block__value body-size-lg">{prettyDate}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="sm:col-span-4 lg:col-span-3 lg:col-end-8">
                              { renderHTML('p', 'listing-card__text body-size-lg', getResultSummaryContent()) }
                            </div>

                            <div className="listing-card__arrow-link">
                                <svg role="presentation" className="icon__arrow icon__arrow--diagonal-animation">
                                    <use xlinkHref="#arrow-diagonal"></use>
                                </svg>
                            </div>
                        </div>
                    </a>
                </div>
            );
        case 'Profile':
            const role = result['listMetadata']['rolePreferred'] ?? result['listMetadata']['role'];

            return (
                <div key={result['rank']} className="listing-card no-wysiwyg">
                    <a href={buildClickTrackingUrl(result['clickTrackingUrl'])} className="listing-card__wrapper heading-link__underline">
                        <div className="listing-card__tag card-tag card-tag--alt body-size-lg">
                            <svg className="card-tag__svg">
                                <use xlinkHref="#square"></use>
                            </svg>
                            <span className="card-tag__text">Our people</span>
                        </div>
                        <div className="listing-card__grid grid sm:grid-cols-8 sm:gap-8 md:gap-16 lg:gap-0">
                            <div className="sm:col-span-4 lg:col-span-3">
                                <h3 className="listing-card__heading heading-size-md">
                                    {result['listMetadata']['namePreferred']} {result['listMetadata']['nameLast']}
                                </h3>

                                <div className="listing-card__metadata metadata-wrapper metadata-wrapper--inline">
                                    <div className="metadata-block metadata-block--inline">
                                        <svg className="metadata-block__svg body-size-lg">
                                            <use xlinkHref="#map-pin"></use>
                                        </svg>
                                        <span className="metadata-block__value body-size-lg">{result['listMetadata']['location']}</span>
                                    </div>
                                    <span className="vertical-divider" role="presentation"></span>
                                    <div className="metadata-block metadata-block--inline">
                                        <span className="metadata-block__value body-size-lg">{role}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="sm:col-span-4 lg:col-span-3 lg:col-end-8">
                              { renderHTML('p', 'listing-card__text body-size-lg', getResultSummaryContent()) }
                            </div>

                            <div className="listing-card__arrow-link">
                                <svg role="presentation" className="icon__arrow icon__arrow--diagonal-animation">
                                    <use xlinkHref="#arrow-diagonal"></use>
                                </svg>
                            </div>
                        </div>
                    </a>
                </div>
            );
        default:
            return (
                <div key={result['rank']} className="listing-card no-wysiwyg">
                    <a href={buildClickTrackingUrl(result['clickTrackingUrl'])} className="listing-card__wrapper heading-link__underline">
                        <div className="listing-card__tag card-tag card-tag--alt body-size-lg">
                            <svg className="card-tag__svg">
                                <use xlinkHref="#square"></use>
                            </svg>
                            <span className="card-tag__text">{type}</span>
                        </div>
                        <div className="listing-card__grid grid sm:grid-cols-8 sm:gap-8 md:gap-16 lg:gap-0">
                            <div className="sm:col-span-4 lg:col-span-3">
                                <h3 className="listing-card__heading heading-size-md">{result['title']}</h3>

                                <div className="listing-card__metadata metadata-wrapper metadata-wrapper--inline">
                                    <div className="metadata-block metadata-block--inline">
                                        <svg className="metadata-block__svg body-size-lg">
                                            <use xlinkHref="#calendar"></use>
                                        </svg>
                                        <span className="metadata-block__value body-size-lg">{prettyDate}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="sm:col-span-4 lg:col-span-3 lg:col-end-8">
                              { renderHTML('p', 'listing-card__text body-size-lg', getResultSummaryContent()) }
                            </div>

                            <div className="listing-card__arrow-link">
                                <svg role="presentation" className="icon__arrow icon__arrow--diagonal-animation">
                                    <use xlinkHref="#arrow-diagonal"></use>
                                </svg>
                            </div>
                        </div>
                    </a>
                </div>
            );
    }
}

/**
 * Create the markup for this people result.
 */
export default React.memo(ResultGlobal);
